import { ARTIFACT_TOOL_NAMES } from "@taqat/core/types";

import type { ChatMessage } from "@/types/chat-message";
import { getEmbedLocale, isEmbedSurface } from "@/lib/embed-locale";
import { CODING_PART_TYPES } from "./coding-parts";
import { isReasoningPartStreaming } from "./reasoning-part-state";
import { formatToolName } from "./tool-call-message/format-tool-name";
import { isToolPartType, toolNameOfPart } from "./tool-call-message/tool-part";

/**
 * Arabic translations for the live activity labels shown in the embed bubble
 * (e.g. the streaming "Working"/"Thinking" indicator). Localized ONLY on the
 * embed surface with an Arabic session — the main app keeps English.
 */
const ACTIVITY_LABELS_AR: Record<string, string> = {
  Working: "جارٍ العمل",
  Thinking: "يفكّر",
  "Tool activity": "نشاط الأدوات",
};
function localizeActivityLabel(label: string): string {
  if (isEmbedSurface() && getEmbedLocale() === "ar") {
    return ACTIVITY_LABELS_AR[label] ?? label;
  }
  return label;
}

type MessagePart = ChatMessage["parts"][number];

/**
 * Tool names whose rendering is user-facing content rather than tool *activity*
 * (the agent asking a question, answering, or rendering a view). They are never
 * folded into a collapsed activity group.
 */
export const CONTENT_TOOL_NAMES = new Set([
  "ask",
  "ask_user_question",
  "complete",
  "render_ui",
]);

/** True when a tool part is content rather than tool activity. */
export function isContentToolPart(part: { type: string; toolName?: string }): boolean {
  const toolName = toolNameOfPart(part);
  return toolName !== undefined && CONTENT_TOOL_NAMES.has(toolName);
}

const ARTIFACT_TOOL_TYPES = new Set([
  `tool-${ARTIFACT_TOOL_NAMES.CREATE}`,
  `tool-${ARTIFACT_TOOL_NAMES.UPDATE}`,
  `tool-${ARTIFACT_TOOL_NAMES.GENERATE_IMAGE}`,
]);

/**
 * `update_plan` renders as the plan panel, never as a transcript row.
 *
 * Same reasoning as the artifact tools above: the call already has a surface of
 * its own, so drawing a chip for it too is the same information twice. It
 * matters more here than it looks — the runtime now asks for the list once at
 * the start of every turn (`core/plan/plan-checkpoint.ts`), so without this an
 * "Update plan" chip would appear in every conversation, including the ones
 * that answer "hi" with an empty list and no panel at all.
 */
const PLAN_TOOL_NAME = "update_plan";

/**
 * Matches the call in both shapes it arrives in.
 *
 * While the call is still streaming it comes through as `dynamic-tool` with the
 * name in a field; once finalized it is `tool-update_plan`. Testing only the
 * finalized shape made the chip flash into the transcript on every turn and
 * then vanish — worse than either state on its own.
 */
export function isPlanToolPart(part: { type: string; toolName?: string }): boolean {
  return toolNameOfPart(part) === PLAN_TOOL_NAME;
}

function isReasoningType(type: string): boolean {
  return type === "reasoning" || type === "reasoning-delta";
}

function reasoningText(part: MessagePart): string {
  const candidate = part as { text?: string; delta?: string; reasoning?: string };
  return candidate.text || candidate.delta || candidate.reasoning || "";
}

/** The tool name behind a tool part, or undefined for any other part. */
function toolNameOf(part: MessagePart): string | undefined {
  return toolNameOfPart(part as { type: string; toolName?: string });
}

/**
 * Names for the coding parts, which are agent activity without being tool calls.
 *
 * A coding turn is mostly these, and a run of them is the same machine work a
 * run of tool calls is — so they group and collapse under one summary like
 * everything else. Without a name here a run of shell commands would never
 * collapse (a group needs at least one named step) and would stack down the
 * transcript at full height.
 */
const CODING_ACTIVITY_NAMES: Record<string, string> = {
  "data-command": "shell",
  "data-file-change": "changes",
  "data-plan": "plan",
};

/** What a part contributes to a run's label: its tool name, or a coding one. */
function activityNameOf(part: MessagePart): string | undefined {
  return toolNameOf(part) ?? CODING_ACTIVITY_NAMES[part.type as string];
}


/**
 * Whether a part is agent *activity* — the internal work (thinking, tool calls)
 * that belongs behind a collapsed summary rather than in the answer itself.
 *
 * Excluded: content tools, artifact tools (they render preview cards), and tool
 * calls carrying chart output (they render the chart inline).
 *
 * The coding parts count too. They are not tool calls, but they are the same
 * kind of machine work — a shell command, a file the agent touched — and a
 * coding turn is mostly them, so leaving them out meant nothing on that surface
 * ever collapsed.
 */
export function isActivityPart(part: MessagePart, showToolActivity: boolean): boolean {
  const type = part.type as string;
  if (isReasoningType(type)) return true;
  if (CODING_PART_TYPES.has(type)) return showToolActivity;
  if (!isToolPartType(type)) return false;
  if (!showToolActivity) return false; // tool activity is hidden entirely — nothing renders
  if (ARTIFACT_TOOL_TYPES.has(type)) return false;
  if (isPlanToolPart(part as { type: string; toolName?: string })) return false;
  if (isContentToolPart(part as { type: string; toolName?: string })) return false;
  if ((part as { output?: { chartData?: unknown } }).output?.chartData) return false;
  return true;
}

/**
 * Whether a part produces no output at all.
 *
 * The stream carries bookkeeping parts the transcript never draws — `step-start`,
 * `data-status`, `data-title`, `data-icon`, `file` (rendered separately, above
 * the parts) — and they sit *between* the tool calls of a single step. They must
 * be transparent to grouping: a run of four searches separated by `data-status`
 * is still one run. Empty reasoning and whitespace-only text draw nothing either.
 *
 * This must not depend on whether the message is still streaming, or a run would
 * be shaped differently mid-stream than it is at rest: the model opens an empty
 * text part between two tool calls, which — if treated as a run-breaker — splits
 * one group into a group plus a stray single row, and then silently re-merges
 * when the stream ends.
 */
export function rendersNothing(part: MessagePart): boolean {
  const type = part.type as string;

  if (isReasoningType(type)) {
    if (isReasoningPartStreaming(part as { type: string; state?: string })) return false;
    const trimmed = reasoningText(part).trim();
    return trimmed.length === 0 || trimmed === "[REDACTED]";
  }

  if (type === "text" || type === "text-delta") {
    return ((part as { text?: string }).text || "").trim().length === 0;
  }

  // Everything renderOnePart actually draws. The coding data parts (spec 083)
  // are real content — omitting them here would let grouping treat a file change
  // or a shell command as invisible bookkeeping and swallow it.
  // `update_plan` is the exception: it is a tool call that draws nothing in the
  // transcript, so grouping must see through it the way it sees through
  // `step-start`. Otherwise the forced call at the top of each turn breaks a
  // run of tool calls into two groups with a gap where nothing renders.
  if (isPlanToolPart(part as { type: string; toolName?: string })) return true;

  if (type === "data-chart" || CODING_PART_TYPES.has(type) || isToolPartType(type)) {
    return false;
  }

  return true;
}

/**
 * Identity of a step for de-duplication. Two consecutive parts with the same
 * signature are the same step repeated (e.g. the model re-issuing an identical
 * search) and collapse into one row with a count.
 */
export function partSignature(part: MessagePart): string {
  const type = part.type as string;
  if (isReasoningType(type)) {
    return `reasoning:${reasoningText(part).trim()}`;
  }
  // Text parts carry no `input`, so they must be keyed on their content or every
  // narration line would look like a repeat of the one before it.
  if (isTextType(type)) {
    return `text:${((part as { text?: string }).text || "").trim()}`;
  }
  const { state, input } = part as { state?: string; input?: unknown };
  // Coding data parts (spec 083) carry their payload on `data`, not `input`.
  // Without this every one of them would sign as `data-command::{}` and two
  // different shell commands would collapse into a single row with a count.
  if (CODING_PART_TYPES.has(type)) {
    return `${type}:${JSON.stringify((part as { data?: unknown }).data ?? {})}`;
  }

  // The name is included explicitly because a dynamic tool does not carry it in
  // its type: two different sandbox tools whose input has not streamed in yet
  // would both sign as `dynamic-tool:input-streaming:{}` and collapse into one
  // row with a count. For a `tool-<name>` part the name is already in the type,
  // so this changes nothing.
  return `${type}:${toolNameOf(part) ?? ""}:${state ?? ""}:${JSON.stringify(input ?? {})}`;
}

/** A single entry inside a group, with the number of identical repeats it absorbed. */
export type ActivityItem = {
  /** Index into `message.parts` — preserved so index-keyed render plans still line up. */
  index: number;
  /** 1 for a normal step; >1 when identical consecutive steps were merged. */
  duplicateCount: number;
};

export type ActivitySegment =
  | { kind: "part"; index: number }
  | {
      kind: "group";
      id: string;
      items: ActivityItem[];
      /** Total steps represented, duplicates included. */
      stepCount: number;
      /** Collapsed summary label, e.g. "Web search". */
      label: string;
      /** True while this run is still being produced. */
      isStreaming: boolean;
      /** Label of the step currently in flight, shown while streaming. */
      currentLabel: string;
    };

export function isTextType(type: string): boolean {
  return type === "text" || type === "text-delta";
}

function labelForPart(part: MessagePart): string {
  const name = activityNameOf(part);
  if (name) return formatToolName(name);
  return localizeActivityLabel(isTextType(part.type as string) ? "Working" : "Thinking");
}

/**
 * Segments a message's parts into standalone parts and runs of agent activity
 * (issue #1458).
 *
 * A run is thinking and tool calls — the machine work the agent does on the way
 * to a message. It collapses into one group when it holds two or more steps and
 * at least one tool call.
 *
 * Any real text the agent emits is a message to the user, never machine chatter,
 * so it is never folded away: a text part always breaks the run. The tool work
 * before it groups on its own, the message renders at full prominence, and the
 * tool work after it starts a fresh group. Artifact previews and content tools
 * (the agent asking a question, rendering a view) stay outside the group too.
 */
export function buildActivitySegments(
  parts: readonly MessagePart[],
  options: { isMessageStreaming: boolean; showToolActivity: boolean },
): ActivitySegment[] {
  const { isMessageStreaming, showToolActivity } = options;
  const segments: ActivitySegment[] = [];

  let run: ActivityItem[] = [];
  let runHasTool = false;
  let runToolNames: string[] = [];
  let lastSignature: string | null = null;

  const flushRun = (endsAtMessageEnd: boolean) => {
    if (run.length === 0) return;

    const stepCount = run.reduce((total, item) => total + item.duplicateCount, 0);

    // A lone step (or a run of pure thinking) keeps its existing standalone
    // rendering — wrapping it would only add a second collapse to click through.
    if (stepCount < 2 || !runHasTool) {
      for (const item of run) segments.push({ kind: "part", index: item.index });
    } else {
      const distinctTools = Array.from(new Set(runToolNames));
      const lastPart = parts[run[run.length - 1].index];
      segments.push({
        kind: "group",
        id: `activity-${run[0].index}`,
        items: run,
        stepCount,
        label: distinctTools.length === 1 ? formatToolName(distinctTools[0]) : localizeActivityLabel("Tool activity"),
        isStreaming: isMessageStreaming && endsAtMessageEnd,
        currentLabel: labelForPart(lastPart),
      });
    }

    run = [];
    runHasTool = false;
    runToolNames = [];
    lastSignature = null;
  };

  parts.forEach((part, index) => {
    // Draws nothing — transparent to grouping. Crucially this keeps the
    // bookkeeping parts the stream interleaves between tool calls (`step-start`,
    // `data-status`, empty text, …) from splitting one run into N single-step runs.
    if (rendersNothing(part)) return;

    // Anything that isn't thinking or a tool call — most importantly a real
    // text message the agent writes — breaks the run and renders on its own.
    // A message the agent emits mid-stream (e.g. "Let me check your workspace
    // and load the web tools.") is never folded into a collapsed group: the
    // tool work before it closes one group, the message shows at full
    // prominence, and the tool work after it opens the next group.
    if (!isActivityPart(part, showToolActivity)) {
      flushRun(false);
      segments.push({ kind: "part", index });
      return;
    }

    const signature = partSignature(part);
    if (signature === lastSignature && run.length > 0) {
      run[run.length - 1].duplicateCount += 1;
      return;
    }

    run.push({ index, duplicateCount: 1 });
    lastSignature = signature;

    const name = activityNameOf(part);
    if (name) {
      runHasTool = true;
      runToolNames.push(name);
    }
  });

  flushRun(true);

  return segments;
}
